import { range } from 'lodash';

import { Option, OptionEntry } from './generate-options';

const toInch = (n: number) => Math.round((n / 2.54) * 2) / 2 + '"';

const toFeet = (n: number) => {
  const realFeet = (n * 0.3937) / 12;
  const feet = Math.floor(realFeet);
  const inches = Math.round((realFeet - feet) * 12);
  return feet + "'" + inches + '"';
};

export const getMeasurementLabel = (value: number, feet = false): string =>
  `${feet ? toFeet(value) : toInch(value)} / ${value} cm`;

export const getMeasurementsOptions = (min: number, max: number, feet = false): OptionEntry[] =>
  range(min, max + 1, 1).map((cur) => ({
    key: cur.toString(),
    value: getMeasurementLabel(cur, feet),
  }));

export const calculateAge = (dateOfBirth: string): number => {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);

  let age = today.getFullYear() - birthDate.getFullYear();

  // Check if the birthday has occurred this year
  const hasBirthdayOccurred =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

  // Adjust age if the birthday hasn't occurred yet this year
  if (!hasBirthdayOccurred) {
    age--;
  }

  return age;
};

export const formatDateToLocalString = (date: string): string => {
  const inputDate = new Date(date);
  return inputDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const getMeasurementValueCM = (value: string) => {
  const centimetersRegex = /(\d+) cm/;
  const centimetersMatch = value.match(centimetersRegex);
  return centimetersMatch ? parseInt(centimetersMatch[1], 10) : 0;
};

export const getMeasurementsOptionsV2 = (min: number, max: number, feet = false): Option[] =>
  range(min, max + 1, 1).map((cur) => ({
    key: getMeasurementLabel(cur, feet),
    value: cur.toString(),
  }));

export const formatNumberWithCommas = (number: number) => {
  return number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const centsToDollars = (cents: number): string => {
  if (isNaN(cents)) {
    throw new Error('Input must be a number');
  }

  const dollars = cents / 100;
  return dollars.toFixed(2);
};

export const getBalance = (balance?: number) => (balance || 0) / 100;

interface RenderCurrencyOpts {
  currency?: string;
  language?: string;
  padZeros?: boolean;
}

export const renderCurrency = (
  value = 0,
  { currency = 'USD', language = 'en-US', padZeros = true }: RenderCurrencyOpts = {},
) => {
  return new Intl.NumberFormat(language, {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: padZeros ? 2 : 0,
    maximumFractionDigits: padZeros ? 2 : 0,
  }).format(value);
};
